import {ReactNode, useEffect, useState} from "react";
import {Slot} from "../../data/Slot";
import {Booking} from "../../data/Booking";
import {DataView} from "primereact/dataview";
import {format} from "date-fns";
import BookingComponent from "./BookingComponent";
import {Panel, PanelHeaderTemplateOptions, PanelProps,} from "primereact/panel";
import {Button} from "primereact/button";
import {Knob} from "primereact/knob";
import {ConfirmPopup, confirmPopup} from "primereact/confirmpopup";
import SlotEditSidebar from "./SlotEditSidebar";

function SlotCompontent({
                            slot,
                            onSlotChanged,
                            onSlotDeleted,
                        }: {
    slot: Slot;
    onSlotChanged(slot: Slot): void;
    onSlotDeleted(slot: Slot): void;
}) {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [bookings, setBookings] = useState<Booking[] | null>(null);
    const [sidebarVisible, setSidebarVisible] = useState(false);


    useEffect(() => {
        setIsLoading(true);

        async function fetchBookings() {
            const response = await fetch(
                `/api/slot/entries/${slot.id}/bookings`,
                {credentials: "include"},
            );
            const resData = await response.json();
            const bookings = resData.map((element: any) => {
                return Object.assign(new Booking(), element);
            });

            setIsLoading(false);
            setBookings(bookings);
        }

        fetchBookings();
    }, [slot]);

    const headerTemplate = (options: PanelHeaderTemplateOptions) => {
        const className = `${options.className} justify-content-space-between`;

        let calculatedAmount = 0;
        if (bookings != null) {
            calculatedAmount = bookings
                .map((b) => b.amount)
                .reduce((a, b) => a + b, 0);
        }

        return (
            <div className={className}>
                <div className="flex flex-column align-items-start gap-0">
                    <p className="m-1 font-bold">
                        {format(slot.startDateTime, "dd.MM.yyyy")}{" "}
                    </p>
                    <p className="m-1">
                        {format(slot.startDateTime, "HH:mm")} -
                        {format(slot.endDateTime, "HH:mm")}
                    </p>
                </div>
                <div className="flex align-items-center gap-2">
                    <Knob
                        readOnly
                        value={calculatedAmount}
                        size={75}
                        valueTemplate={"{value}/" + slot.capacity}
                        max={slot.capacity}
                        valueColor="var(--red-500)"
                        rangeColor="var(--green-300)"
                    />
                    {slot.disabled && (
                        <i className="pi pi-lock text-gray-500 text-xl"/>
                    )}
                </div>
                {options.togglerElement}
            </div>
        );
    };

    function onBookingChanged(updatedBooking: Booking): void {
        if (!bookings) return;

        const updatedBookings = bookings.map((booking) =>
            booking.id === updatedBooking.id ? updatedBooking : booking,
        );
        setBookings(updatedBookings);
    }

    function onBookingDeleted(booking: Booking): void {
        const newBookings = bookings!.filter((b) => b.id !== booking.id);
        setBookings(newBookings);
    }

    function itemTemplate(item: Booking): ReactNode {
        return (
            <BookingComponent
                booking={item}
                onBookingChanged={onBookingChanged}
                onBookingDeleted={onBookingDeleted}
            ></BookingComponent>
        );
    }

    const listTemplate = (items: Booking[]) => {
        return (
            <div className="grid grid-nogutter flex-column">
                {items.map((item) => itemTemplate(item))}
            </div>
        );
    };

    async function deleteSlot() {
        await fetch(
            `/api/slot/entries/${slot.id}`,
            {
                credentials: "include",
                method: "DELETE",
            },
        );

        onSlotDeleted(slot);
    }

    const openSidebar = () => {
        setSidebarVisible(true);
    };

    const saveChanges = async (newSlot: Slot) => {
        if (!newSlot) return;

        console.log(JSON.stringify(newSlot))

        try {
            const response = await fetch(
                `/api/slot/entries/${slot.id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify(newSlot),
                },
            );

            if (response.ok) {
                onSlotChanged(newSlot);
                setSidebarVisible(false);
            } else {
                console.error("Failed to update slot");
            }
        } catch (error) {
            console.error("Error updating slot:", error);
        }
    };

    const accept = () => {
        deleteSlot();
    };

    const reject = () => {
    };

    const confirmDelete = (event: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: "Willst du den Termin wirklich löschen?",
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "accept",
            accept,
            reject,
        });
    };

    const footerTemplate = (options: PanelProps) => {
        const className = `${options.className} flex flex-wrap justify-content-end align-items-center gap-3`;
        return (
            <div className={className}>
                {slot.comment && <div className="flex flex-1">{slot.comment}</div>}
                <Button
                    icon="pi pi-pencil"
                    severity="info"
                    loading={isLoading}
                    className="p-button-rounded p-button-text"
                    onClick={openSidebar}
                />
                <Button
                    icon="pi pi-trash"
                    severity="danger"
                    loading={isLoading}
                    className="p-button-rounded p-button-text"
                    disabled={bookings === null || bookings.length > 0}
                    onClick={confirmDelete}
                />
            </div>
        );
    };

    return (
        <div>
            <ConfirmPopup/>

            <SlotEditSidebar
                visible={sidebarVisible}
                onHide={() => setSidebarVisible(false)}
                slot={slot}
                onSave={saveChanges}
            />
            <Panel
                className="w-full"
                headerTemplate={headerTemplate}
                footerTemplate={footerTemplate}
                toggleable
                collapsed
            >
                <DataView value={bookings || undefined} listTemplate={listTemplate}/>
                <p className="text-left text-xs text-secondary">{slot.id}</p>
            </Panel>
        </div>
    );
}

export default SlotCompontent;
